import { z } from 'zod';
import { SCOPES } from './permissions/scope';

/**
 * Request bodies, validated once at the API boundary. The console imports the same
 * schemas so a form shows the server's rules before it is ever submitted.
 */

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const id = z.uuid();
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Renk #RRGGBB biçiminde olmalı');
/** Every update carries the version it was read at, so a stale form cannot overwrite. */
const version = z.number().int().positive();

export const email = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Geçerli bir e-posta adresi girin'));
export const password = z
  .string()
  .min(10, 'Şifre en az 10 karakter olmalı')
  .max(200, 'Şifre en fazla 200 karakter olabilir');

export const slug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/,
    'Kısa ad 3–40 karakter; küçük harf, rakam ve tire içerebilir',
  );

export const loginInput = z.object({
  email,
  password: z.string().min(1, 'Şifrenizi girin'),
});

export const switchTenantInput = z.object({ tenantId: id });

export const changePasswordInput = z.object({
  currentPassword: z.string().min(1),
  newPassword: password,
});

export const updateProfileInput = z.object({ name: trimmed(120) });

export const tenantSettingsInput = z.object({
  version,
  name: trimmed(120).optional(),
  timezone: trimmed(60).optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  brandColor: color.nullable().optional(),
  // Kurumsal kimlik (ADR 0016): what customers see on proposals and packages.
  legalName: optionalText(200),
  contactPhone: optionalText(40),
  contactEmail: z
    .union([z.literal(''), z.email('Geçerli bir e-posta yazın')])
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
  website: optionalText(200),
  address: optionalText(500),
  // ADR 0019: whether customer payments are followed in the program.
  tracksCollections: z.boolean().optional(),
});

const companyFields = {
  name: trimmed(120),
  legalName: optionalText(200),
  taxOffice: optionalText(80),
  taxNumber: optionalText(20),
  mersisNumber: optionalText(20),
  phone: optionalText(40),
  email: z
    .union([z.literal(''), email])
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  address: optionalText(400),
  city: optionalText(80),
};

export const createCompanyInput = z.object({ ...companyFields, isDefault: z.boolean().optional() });
export const updateCompanyInput = z
  .object({ ...companyFields, isDefault: z.boolean() })
  .partial()
  .extend({ version });

const departmentFields = {
  name: trimmed(80),
  description: optionalText(300),
  color,
  companyId: id.nullable(),
  parentId: id.nullable(),
};

export const createDepartmentInput = z.object({
  ...departmentFields,
  companyId: id.nullable().optional(),
  parentId: id.nullable().optional(),
});
export const updateDepartmentInput = z.object(departmentFields).partial().extend({ version });

const grant = z.object({ permission: z.string().min(1), scope: z.enum(SCOPES) });

export const createRoleInput = z.object({
  name: trimmed(60),
  description: optionalText(300),
  color,
  grants: z.array(grant).max(500).default([]),
});
export const updateRoleInput = z
  .object({
    name: trimmed(60),
    description: optionalText(300),
    color,
    grants: z.array(grant).max(500),
  })
  .partial()
  .extend({ version });

const override = z.object({
  permission: z.string().min(1),
  effect: z.enum(['grant', 'deny']),
  scope: z.enum(SCOPES).nullable(),
});

export const updateMemberInput = z
  .object({
    title: optionalText(80),
    status: z.enum(['active', 'suspended']),
    /** Roles that hold in every company. */
    roleIds: z.array(id).max(20),
    /** Roles in one company only (ADR 0017); a company left out is reached only by roleIds. */
    companyRoles: z.array(z.object({ companyId: id, roleIds: z.array(id).max(20) })).max(50),
    departments: z.array(z.object({ departmentId: id, isLead: z.boolean() })).max(50),
    primaryDepartmentId: id.nullable(),
    overrides: z.array(override).max(200),
  })
  .partial()
  .extend({ version });

export const createInvitationInput = z.object({
  email,
  name: optionalText(120),
  roleId: id,
  departmentId: id.nullable().optional(),
});

export const acceptInvitationInput = z.object({
  token: z.string().min(20).max(200),
  name: trimmed(120).optional(),
  password: password.optional(),
});

export const createTenantInput = z.object({
  name: trimmed(120),
  slug,
  plan: z.enum(['trial', 'standard', 'pro']).default('trial'),
  ownerEmail: email,
  ownerName: optionalText(120),
});

export const updateTenantStatusInput = z.object({
  status: z.enum(['trial', 'active', 'suspended', 'closed']),
});

export type LoginInput = z.infer<typeof loginInput>;
export type TenantSettingsInput = z.input<typeof tenantSettingsInput>;
export type CreateCompanyInput = z.input<typeof createCompanyInput>;
export type UpdateCompanyInput = z.input<typeof updateCompanyInput>;
export type CreateDepartmentInput = z.input<typeof createDepartmentInput>;
export type UpdateDepartmentInput = z.input<typeof updateDepartmentInput>;
export type CreateRoleInput = z.input<typeof createRoleInput>;
export type UpdateRoleInput = z.input<typeof updateRoleInput>;
export type UpdateMemberInput = z.input<typeof updateMemberInput>;
export type CreateInvitationInput = z.input<typeof createInvitationInput>;
export type AcceptInvitationInput = z.input<typeof acceptInvitationInput>;
export type CreateTenantInput = z.input<typeof createTenantInput>;
